import React from "react";
import { supportEventLogger } from "../services/SupportEventLogger";
import { logger } from "./logger";
import { ErrorFactory, DooDatesError } from "./error-handling";
import { captureError, addBreadcrumb, setUserContext } from "./sentry";

/**
 * Gestionnaire d'erreurs global pour l'application
 * Capture automatiquement les erreurs et les logue dans le système de support
 */

/**
 * Détecte les AbortError bénins (fetch annulé volontairement) pour éviter
 * de polluer les logs. Les abandons viennent typiquement de :
 * - Supabase realtime / auth polling
 * - composants démontés avant la fin d'un fetch
 * - timeouts internes gérés
 */
export function isBenignAbortError(err: unknown): boolean {
  if (!err) return false;
  const anyErr = err as { name?: string; message?: string };
  if (anyErr.name === "AbortError") return true;
  const msg = (anyErr.message || String(err)).toLowerCase();
  return (
    msg.includes("signal is aborted") ||
    msg.includes("the operation was aborted") ||
    msg.includes("aborterror")
  );
}

// Codes d'erreur standardisés pour le support
export const SUPPORT_ERROR_CODES = {
  // Authentification
  AUTH_LOGIN_FAILED: "auth_login_failed",
  AUTH_SIGNUP_FAILED: "auth_signup_failed",
  AUTH_TOKEN_EXPIRED: "auth_token_expired",
  AUTH_SESSION_INVALID: "auth_session_invalid",

  // Base de données
  DB_CONNECTION_FAILED: "db_connection_failed",
  DB_QUERY_FAILED: "db_query_failed",
  DB_TIMEOUT: "db_timeout",
  DB_CONSTRAINT_VIOLATION: "db_constraint_violation",

  // API externes
  API_GEMINI_FAILED: "api_gemini_failed",
  API_GEMINI_TIMEOUT: "api_gemini_timeout",
  API_GEMINI_QUOTA_EXCEEDED: "api_gemini_quota_exceeded",
  API_EMAIL_FAILED: "api_email_failed",
  API_WEBHOOK_FAILED: "api_webhook_failed",

  // Fonctionnalités utilisateur
  POLL_CREATION_FAILED: "poll_creation_failed",
  POLL_VOTE_FAILED: "poll_vote_failed",
  POLL_EXPORT_FAILED: "poll_export_failed",
  CALENDAR_SYNC_FAILED: "calendar_sync_failed",

  // Performance
  PERF_SLOW_LOAD: "perf_slow_load",
  PERF_MEMORY_HIGH: "perf_memory_high",
  PERF_LARGE_RESPONSE: "perf_large_response",

  // UI/UX
  UI_COMPONENT_CRASH: "ui_component_crash",
  UI_RENDER_ERROR: "ui_render_error",
  UI_FORM_VALIDATION_FAILED: "ui_form_validation_failed",

  // Système
  SYSTEM_UNKNOWN_ERROR: "system_unknown_error",
  SYSTEM_CONFIG_ERROR: "system_config_error",
  SYSTEM_MAINTENANCE_MODE: "system_maintenance_mode",
} as const;

export type SupportErrorCode =
  (typeof SUPPORT_ERROR_CODES)[keyof typeof SUPPORT_ERROR_CODES];

/**
 * Error boundary pour React - capture les erreurs de composants
 */
export class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  /**
   * Initialise la capture globale d'erreurs
   */
  public initialize(): void {
    if (this.isInitialized) return;

    // Capturer les erreurs JavaScript non gérées
    window.addEventListener("error", this.handleGlobalError.bind(this));

    // Capturer les promesses rejetées non gérées
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection.bind(this),
    );

    // Surveiller les erreurs React (si disponible)
    if (
      typeof window !== "undefined" &&
      (window as any).__REACT_ERROR_OVERLAY_GLOBAL_HOOK__
    ) {
      const originalHook = (window as any).__REACT_ERROR_OVERLAY_GLOBAL_HOOK__;
      (window as any).__REACT_ERROR_OVERLAY_GLOBAL_HOOK__ = (error: Error) => {
        this.logReactError(error);
        if (originalHook) originalHook(error);
      };
    }

    this.isInitialized = true;
    logger.info("Global error handler initialized");
  }

  /**
   * Gère les erreurs JavaScript globales
   */
  private handleGlobalError(event: ErrorEvent): void {
    const error = new Error(event.message);
    error.stack = event.error?.stack;

    if (isBenignAbortError(error) || isBenignAbortError(event.error)) {
      return;
    }

    this.logError(error, SUPPORT_ERROR_CODES.SYSTEM_UNKNOWN_ERROR, {
      component: "GlobalErrorHandler",
      action: "global_error",
      params: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  }

  /**
   * Gère les promesses rejetées non gérées
   */
  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    if (isBenignAbortError(error) || isBenignAbortError(event.reason)) {
      // Fetch abandons volontaires (Supabase realtime polling, timeouts gérés,
      // composants démontés) ne sont pas des bugs. Swallow silently.
      event.preventDefault?.();
      return;
    }

    this.logError(error, SUPPORT_ERROR_CODES.SYSTEM_UNKNOWN_ERROR, {
      component: "GlobalErrorHandler",
      action: "unhandled_promise_rejection",
      params: {
        reason: event.reason,
      },
    });
  }

  /**
   * Détermine si une erreur est critique pour le business
   */
  private isBusinessCritical(code: SupportErrorCode): boolean {
    const businessCriticalCodes = [
      // Erreurs qui affectent plusieurs utilisateurs
      SUPPORT_ERROR_CODES.POLL_CREATION_FAILED,
      SUPPORT_ERROR_CODES.POLL_VOTE_FAILED,
      SUPPORT_ERROR_CODES.POLL_EXPORT_FAILED,
      SUPPORT_ERROR_CODES.CALENDAR_SYNC_FAILED,

      // Erreurs système critiques
      SUPPORT_ERROR_CODES.DB_CONNECTION_FAILED,
      SUPPORT_ERROR_CODES.DB_TIMEOUT,
      SUPPORT_ERROR_CODES.API_GEMINI_FAILED,
      SUPPORT_ERROR_CODES.API_EMAIL_FAILED,

      // Authentification étendue
      SUPPORT_ERROR_CODES.AUTH_LOGIN_FAILED,
      SUPPORT_ERROR_CODES.AUTH_SIGNUP_FAILED,
    ];

    return (businessCriticalCodes as string[]).includes(code);
  }

  /**
   * Logue une erreur spécifique au support
   */
  public async logError(
    error: Error,
    code: SupportErrorCode,
    context?: {
      component?: string;
      action?: string;
      params?: Record<string, any>;
      state?: Record<string, any>;
    },
  ): Promise<void> {
    try {
      // 1. Toujours envoyer à Sentry (technique)
      captureError(error, {
        errorCode: code,
        component: context?.component,
        action: context?.action,
        ...context?.params,
        ...context?.state,
      });

      // 2. Ajouter un breadcrumb pour le contexte
      addBreadcrumb({
        message: `Error: ${code}`,
        level: "error",
        data: {
          errorCode: code,
          component: context?.component,
          action: context?.action,
        },
      });

      // 3. Si business-critical, aussi logger dans notre système
      if (this.isBusinessCritical(code)) {
        await supportEventLogger.logError(error, code, context);
      }
    } catch (logError) {
      // Fallback: logger local si le support logging échoue
      console.error("Failed to log to support:", logError);
      console.error("Original error:", error);
    }
  }

  /**
   * Logue une erreur React
   */
  private logReactError(error: Error): void {
    this.logError(error, SUPPORT_ERROR_CODES.UI_COMPONENT_CRASH, {
      component: "React",
      action: "component_error",
    });
  }

  /**
   * Logue une erreur de performance
   */
  public async logPerformance(
    metric: string,
    value: number,
    context?: {
      component?: string;
      params?: Record<string, any>;
    },
  ): Promise<void> {
    try {
      // 1. Envoyer à Sentry (performance monitoring)
      addBreadcrumb({
        message: `Performance: ${metric}`,
        level: value > 5000 ? "error" : value > 2000 ? "warning" : "info",
        data: {
          metric,
          value,
          component: context?.component,
          ...context?.params,
        },
      });

      // 2. Si c'est lent, aussi logger dans notre système (business impact)
      if (value > 3000) {
        await supportEventLogger.logPerformance(metric, value, context);
      }
    } catch (error) {
      console.error("Failed to log performance:", error);
    }
  }

  /**
   * Logue une action utilisateur
   */
  public async logUserAction(
    action: string,
    component: string,
    details?: Record<string, any>,
  ): Promise<void> {
    try {
      // 1. Envoyer à Sentry (breadcrumbs pour le contexte)
      addBreadcrumb({
        message: `User action: ${action}`,
        level: "info",
        data: {
          action,
          component,
          ...details,
        },
      });

      // 2. Pas de logging dans notre système pour les actions utilisateur (trop verbeux)
    } catch (error) {
      console.error("Failed to log user action:", error);
    }
  }
}

// Instance globale
export const globalErrorHandler = GlobalErrorHandler.getInstance();

// Fonctions utilitaires pour usage courant
export const logSupportError = (
  error: Error,
  code: SupportErrorCode,
  context?: {
    component?: string;
    action?: string;
    params?: Record<string, any>;
  },
) => globalErrorHandler.logError(error, code, context);

export const logSupportPerformance = (
  metric: string,
  value: number,
  context?: {
    component?: string;
    params?: Record<string, any>;
  },
) => globalErrorHandler.logPerformance(metric, value, context);

export const logSupportUserAction = (
  action: string,
  component: string,
  details?: Record<string, any>,
) => globalErrorHandler.logUserAction(action, component, details);

/**
 * Hook pour intégrer le logging dans les composants React
 */
export const useSupportLogging = () => {
  return {
    logError: logSupportError,
    logPerformance: logSupportPerformance,
    logUserAction: logSupportUserAction,
  };
};

/**
 * Wrapper pour les fonctions async avec logging d'erreurs
 */
export function withAsyncErrorLogging<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  context: {
    component?: string;
    action: string;
    errorCode: SupportErrorCode;
  },
) {
  return async (...args: T): Promise<R> => {
    try {
      const startTime = performance.now();
      const result = await fn(...args);
      const duration = performance.now() - startTime;

      // Logger la performance si c'est lent
      if (duration > 1000) {
        logSupportPerformance(
          `${context.action}_duration`,
          Math.round(duration),
          context,
        );
      }

      return result;
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      await logSupportError(errorObj, context.errorCode, context);
      throw error;
    }
  };
}
